import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import {
  listTeachers,
  updateTeacher,
  listFrequencies,
  listSubjectsByLevel,
} from "../services/teacher";
import { getUserInfo } from "../services/auth";

const EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const subjectIdsFrom = (teacher) =>
  (teacher?.subject || teacher?.modules || []).map((s) => s.id);

const fieldsFromTeacher = (teacher) => ({
  name: teacher?.name || "",
  email: teacher?.email || "",
  contact: teacher?.contact || "",
  birthDate: teacher?.birthDate ? teacher.birthDate.split("T")[0] : "",
  maritalStatus: teacher?.maritalStatus || "Solteiro",
  educationalLevel: teacher?.educationLevel || "Ensino Médio",
  selectedSubjects: subjectIdsFrom(teacher),
});

const EditarProfessorLayer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();

  const [teacher, setTeacher] = useState(location.state?.teacher || null);
  const [loading, setLoading] = useState(!location.state?.teacher);
  const [saving, setSaving] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [form, setForm] = useState(fieldsFromTeacher(location.state?.teacher));

  useEffect(() => {
    listFrequencies()
      .then((resp) => Promise.all((resp.data || []).map((f) => listSubjectsByLevel(f.id))))
      .then((results) => setSubjects(results.flatMap((resp) => resp.data || [])))
      .catch(() => setSubjects([]));
  }, []);

  useEffect(() => {
    if (teacher) return;
    listTeachers()
      .then((resp) => {
        const found = (resp.data || []).find((w) => String(w.id) === String(id));
        setTeacher(found || null);
        if (found) setForm(fieldsFromTeacher(found));
      })
      .catch(() => setTeacher(null))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const setField = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const toggleSubject = (subjectId) => {
    setForm((f) => ({
      ...f,
      selectedSubjects: f.selectedSubjects.includes(subjectId)
        ? f.selectedSubjects.filter((s) => s !== subjectId)
        : [...f.selectedSubjects, subjectId],
    }));
  };

  const selectedSubjectNames = useMemo(
    () =>
      subjects
        .filter((s) => form.selectedSubjects.includes(s.id))
        .map((s) => s.subjectName),
    [subjects, form.selectedSubjects],
  );

  const handleSave = async () => {
    if (!form.name || !form.contact) {
      toast.error(t("validation.required"));
      return;
    }
    if (form.contact.length !== 9) {
      toast.error(t("validation.required"));
      return;
    }
    if (form.email && !EMAIL_PATTERN.test(form.email)) {
      toast.error(t("validation.email.invalid"));
      return;
    }
    setSaving(true);
    const subjectPayload = subjects
      .filter((s) => form.selectedSubjects.includes(s.id))
      .map((s) => ({ id: s.id, name: s.subjectName }));
    const payload = {
      name: form.name,
      email: form.email,
      contact: form.contact,
      birthDate: form.birthDate,
      maritalStatus: form.maritalStatus,
      educationalLevel: form.educationalLevel,
      subject: subjectPayload,
      modules: subjectPayload,
      isPermanent: "Sim",
      country: "Mozambique",
      updatedBy: getUserInfo()?.id,
    };
    try {
      await updateTeacher(id, payload);
      toast.success(t("professor.edit.success"));
      navigate("/professores/lista");
    } catch (err) {
      toast.error(t("global.error.message"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className='card'>
        <div className='card-body text-secondary-light'>{t("professor.list.loading")}</div>
      </div>
    );
  }

  if (!teacher) {
    return (
      <div className='card'>
        <div className='card-body text-center py-64'>
          <p className='text-secondary-light'>{t("professor.edit.not.found")}</p>
          <button
            type='button'
            className='btn btn-primary-600 px-32'
            onClick={() => navigate("/professores/lista")}
          >
            <i className='ri-arrow-left-line' /> {t("professor.edit.back.to.list")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className='row gy-4'>
      <ToastContainer />
      <div className='col-lg-4'>
        <div className='user-grid-card position-relative border radius-16 overflow-hidden bg-base h-100'>
          <img
            src='assets/images/user-grid/user-grid-bg1.png'
            alt=''
            className='w-100 object-fit-cover'
          />
          <div className='pb-24 ms-16 mb-24 me-16 mt--100'>
            <div className='text-center border border-top-0 border-start-0 border-end-0'>
              <img
                src='assets/images/user-grid/user-grid-img14.png'
                alt=''
                className='border br-white border-width-2-px w-200-px h-200-px rounded-circle object-fit-cover'
              />
              <h6 className='mb-0 mt-16'>{teacher.name}</h6>
              <span className='text-secondary-light mb-16'>{teacher.email || "-"}</span>
            </div>
            <div className='mt-24'>
              <h6 className='text-xl mb-16'>{t("global.personal.information")}</h6>
              <ul>
                <li className='d-flex align-items-center gap-1 mb-12'>
                  <span className='w-50 text-md fw-semibold text-primary-light'>
                    {t("global.contact")}
                  </span>
                  <span className='w-50 text-secondary-light fw-medium'>
                    : {teacher.contact || "-"}
                  </span>
                </li>
                <li className='d-flex align-items-center gap-1 mb-12'>
                  <span className='w-50 text-md fw-semibold text-primary-light'>
                    {t("global.register.date")}
                  </span>
                  <span className='w-50 text-secondary-light fw-medium'>
                    : {teacher.createdAt ? teacher.createdAt.split("T")[0] : "-"}
                  </span>
                </li>
                <li className='d-flex align-items-center gap-1'>
                  <span className='w-50 text-md fw-semibold text-primary-light'>
                    {t("professor.field.subjects")}
                  </span>
                  <span className='w-50 text-secondary-light fw-medium'>
                    : {selectedSubjectNames.join(", ") || "-"}
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className='col-lg-8'>
        <div className='card h-100'>
          <div className='card-body p-24'>
            <h6 className='text-md text-primary-light mb-16'>{t("professor.edit.title")}</h6>
            <p className='text-secondary-light text-sm mb-24'>{t("professor.edit.subtitle")}</p>

            <div className='row'>
              <div className='col-sm-6'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("professor.field.name")} <span className='text-danger-600'>*</span>
                  </label>
                  <div className="icon-field">
                    <span className="icon">
                      <Icon icon="f7:person" />
                    </span>
                    <input
                      type='text'
                      className='form-control radius-8'
                      value={form.name}
                      onChange={setField("name")}
                    />
                  </div>
                </div>
              </div>
              <div className='col-sm-6'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("professor.field.email")}
                  </label>
                  <div className="icon-field">
                    <span className="icon">
                      <Icon icon="mage:email" />
                    </span>
                    <input
                      type='email'
                      className='form-control radius-8'
                      value={form.email}
                      onChange={setField("email")}
                    />
                  </div>
                </div>
              </div>
              <div className='col-sm-4'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("professor.field.contact")} <span className='text-danger-600'>*</span>
                  </label>
                  <div className="icon-field">
                    <span className="icon">
                      <Icon icon="solar:phone-calling-linear" />
                    </span>
                    <input
                      type='text'
                      maxLength={9}
                      className='form-control radius-8'
                      value={form.contact}
                      onChange={setField("contact")}
                    />
                  </div>
                </div>
              </div>
              <div className='col-sm-4'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("professor.field.birthDate")}
                  </label>
                  <div className="icon-field">
                    <span className="icon">
                      <Icon icon="solar:calendar-outline" />
                    </span>
                    <input
                      type='date'
                      className='form-control radius-8'
                      value={form.birthDate}
                      onChange={setField("birthDate")}
                    />
                  </div>
                </div>
              </div>
              <div className='col-sm-4'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("professor.field.maritalStatus")}
                  </label>
                  <select
                    className='form-control radius-8 form-select'
                    value={form.maritalStatus}
                    onChange={setField("maritalStatus")}
                  >
                    <option value='Solteiro'>{t("global.single")}</option>
                    <option value='Casado'>{t("global.married")}</option>
                    <option value='Divorciado'>{t("global.divorced")}</option>
                    <option value='Viuvo'>{t("global.widower")}</option>
                  </select>
                </div>
              </div>
              <div className='col-sm-6'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("professor.field.educationalLevel")}
                  </label>
                  <select
                    className='form-control radius-8 form-select'
                    value={form.educationalLevel}
                    onChange={setField("educationalLevel")}
                  >
                    <option value='Ensino Médio'>{t("global.middle.school")}</option>
                    <option value='Ensino Superior'>{t("global.bachelor")}</option>
                    <option value='Pós-graduação'>{t("global.post.graduated")}</option>
                    <option value='Mestrado'>{t("global.master")}</option>
                  </select>
                </div>
              </div>
              <div className='col-sm-12'>
                <div className='mb-20'>
                  <label className='form-label fw-semibold text-primary-light text-sm mb-8'>
                    {t("professor.field.subjects")}
                  </label>
                  <div className='d-flex flex-wrap gap-8'>
                    {subjects.map((subject) => {
                      const active = form.selectedSubjects.includes(subject.id);
                      return (
                        <button
                          type='button'
                          key={subject.id}
                          onClick={() => toggleSubject(subject.id)}
                          className={`btn btn-sm radius-8 ${
                            active ? "btn-primary-600" : "btn-outline-secondary"
                          }`}
                        >
                          {subject.subjectName}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            <div className='d-flex align-items-center justify-content-center gap-3'>
              <button
                type='button'
                className='border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-56 py-11 radius-8'
                onClick={() => navigate("/professores/lista")}
                disabled={saving}
              >
                {t("global.cancel")}
              </button>
              <button
                type='button'
                className='btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8'
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "..." : <><i className='ri-save-line' /> {t("global.save")}</>}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditarProfessorLayer;
